import { useEffect, useRef, useState } from 'react';

import { useRouteMatch } from 'react-router-dom';
import { validator, isValid, errors } from 'react-validator';
import { useTranslation } from 'react-i18next';

import {
  useInsertTeachingStructureItemMutation,
  useUpdateTeachingStructureItemMutation,
} from '@school_ify/schoolify-repository/src/teacher/types';

import useRouterHelper from 'hooks/useRouterHelper';
import Errors from 'components/Errors/Errors';

import IStudyQuery from '../../IURLQuery';

import './NewStructureItem.scoped.scss';

const NewStructureItem: React.FC<{
  // eslint-disable-next-line @typescript-eslint/ban-types
  callback: Function;
  value?: string;
}> = ({ callback, value }) => {
  const { query, routePush } = useRouterHelper<IStudyQuery>();
  const [validationError, setValidationError] = useState({});
  const { params } = useRouteMatch<{
    id: string;
  }>();
  const { t } = useTranslation();
  const [model, setModel] = useState('');
  const [insertTeachingStructureItemMutation, { error }] = useInsertTeachingStructureItemMutation();
  const [updateTeachingStructureItemMutation, { error: updateError }] =
    useUpdateTeachingStructureItemMutation();

  const addNewTeachingStructureInputRef = useRef(null);

  const removeAddNewParamFromUrl = () => {
    routePush({
      removeSearch: ['newItem', 'edit'],
    });
  };

  const addNew = async () => {
    let parentId: string = params.id;
    if (query.lesson) {
      parentId = query.lesson as string;
    }

    await insertTeachingStructureItemMutation({
      variables: {
        title: model,
        parent_id: parentId,
      },
    });
    if (error) {
      throw error;
      return;
    }
  };

  const updateExisting = async () => {
    await updateTeachingStructureItemMutation({
      variables: {
        title: model,
        id: query.lesson as string,
      },
    });

    if (updateError) {
      throw updateError;
    }
    callback();
  };

  const handleSave = async () => {
    validator(
      {
        title: model,
      },
      {
        title: ['required'],
      },
      {
        'title.required': t('study:title_required'),
      }
    );

    if (!isValid()) {
      setValidationError(errors());
      return;
    }

    if (query && query.newItem) {
      await addNew();
    }
    if (query && query.edit) {
      await updateExisting();
    }
    removeAddNewParamFromUrl();
    callback();
  };

  const handleCancel = () => {
    return () => removeAddNewParamFromUrl();
  };

  useEffect(() => {
    setModel(value);
    addNewTeachingStructureInputRef.current.focus();
  }, []);

  return (
    <>
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSave();
        }}
        className="add-new-structure-item"
      >
        <input
          placeholder={t('study:enter_title')}
          value={model}
          ref={addNewTeachingStructureInputRef}
          onChange={e => setModel(e.target.value)}
        />
        <div onClick={() => handleSave()} className="button add">
          &#10003;
        </div>
        <div onClick={handleCancel()} className="text button cancel">
          x
        </div>
      </form>
      <div className="pl-1">
        <Errors errors={validationError} prop="title" />
      </div>
    </>
  );
};

export default NewStructureItem;
